import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { InstantSearch, Configure, Index } from 'react-instantsearch-dom';
import { noEmptySearchClient } from 'components/essentials/AlgoliaSearchClient';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import useOpenSearch from 'global-state/isSearchOpen/useOpenSearch';
import useIsSearchOpen from 'global-state/isSearchOpen/useIsSearchOpen';
import useCloseSearch from 'global-state/isSearchOpen/useCloseSearch';
import useIsAdvancedSearch from './hooks/useIsAdvancedSearch';
import { CategoryHits, ResultsByCategory } from './commons/helpers';
import SearchResultsWrapper from './SearchResultsWrapper';
import ProductGrid from './ProductGrid';
import SeeFullResultsButton from './SeeFullResultsButton';
import JBSearchBox from './components/JBSearchBox';

/**
 * Main consumer search results component
 *
 * Features:
 * - 3-column responsive layout (Products, Categories, View Results By)
 * - Product grid with hover effects and pricing
 * - Dynamic hit counts and search state management
 * - Accessibility improvements and performance optimizations
 */
const ConsumerSearchBar = () => {
  const [isSearchBarActivated, setisSearchBarActivated] = useState(false);

  const closeSearch = useCloseSearch();
  const openSearch = useOpenSearch();
  const isSearchOpen = useIsSearchOpen();
  const isAdvancedSearch = useIsAdvancedSearch();

  const handleOverlayKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        closeSearch();
      }
    },
    [closeSearch]
  );

  const handleSearbarFocus = useCallback(() => {
    setisSearchBarActivated(true);
    openSearch();
  }, [setisSearchBarActivated, openSearch]);

  return (
    <div
      className={classNames(
        'bg-white max-[1023px]:relative max-[1023px]:z-10',
        {
          hidden: isAdvancedSearch,
        }
      )}
    >
      {/* Search Modal */}
      <div
        className="bg-white relative max-[1023px]:z-10 lg:!z-[1002]"
        onClick={handleSearbarFocus}
        role="button"
        tabIndex={0}
      >
        <InstantSearch
          searchClient={noEmptySearchClient}
          indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
        >
          <Configure
            hitsPerPage={3}
            facets={['*']}
            numericFilters={[
              'item_availability<2',
              'visibility>0',
              'visibility<4',
            ]}
            attributesToRetrieve={['_highlightResult', 'product_id', 'name']}
          />

          <JBSearchBox
            autoFocus
            isActive
            setInactive={() => {}}
            toggleSearchOpen={closeSearch}
            variant="ConsumerInstantSearch"
          />
          {isSearchOpen && (
            <SearchResultsWrapper>
              <div className="bg-white absolute w-full">
                <div className="flex w-full max-w-none mx-auto px-2 gap-6">
                  <div className="flex-1">
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <ProductGrid />
                      <div className="mt-6 flex justify-center">
                        <SeeFullResultsButton />
                      </div>
                    </div>
                  </div>

                  <div className="w-64">
                    <div className="bg-gray-50 p-0 rounded-lg h-full">
                      <Index indexName="categories">
                        <Configure
                          hitsPerPage={5}
                          numericFilters={['fk_store_id=1']}
                          facetFilters={['is_searchable:1']}
                        />
                        <CategoryHits toggleSearchOpen={closeSearch} />
                      </Index>
                    </div>
                  </div>

                  <div className="w-64">
                    <div className="bg-gray-50 p-6 rounded-lg h-full">
                      <Index indexName="reviews">
                        <Configure
                          facets={['*']}
                          numericFilters=""
                          facetFilters=""
                        />
                      </Index>
                      <Index indexName="customer_photos">
                        <Configure facets={['*']} numericFilters="" />
                      </Index>
                      <ResultsByCategory toggleSearchOpen={closeSearch} />
                    </div>
                  </div>
                </div>
              </div>
            </SearchResultsWrapper>
          )}
        </InstantSearch>
      </div>
      {/* Overlay Background */}
      <div
        className={classNames(
          'fixed inset-0 bg-blackOverlay z-[1001] transition-opacity duration-300 ease-out',
          {
            hidden: !isSearchOpen || !isSearchBarActivated,
            block: isSearchOpen && isSearchBarActivated,
          }
        )}
        onClick={closeSearch}
        role="button"
        tabIndex={0}
        onKeyDown={handleOverlayKeyDown}
        aria-label="Close search modal"
      />
    </div>
  );
};

export default ConsumerSearchBar;
